import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n";

const CONTENT = {
    it: {
        overline: "Tartufi & Funghi",
        title: "Il tartufo più fresco, selezionato per te",
        intro: "Ogni settimana selezioniamo il tartufo più fresco grazie a una rete di circa 50 punti di raccolta tra Italia ed Estero: seguiamo la stagionalità dei territori per garantire sempre il prodotto migliore, dal bianco di Alba al nero pregiato ed estivo.",
        criteriaTitle: "Come selezioniamo il migliore tartufo fresco",
        criteria: [
            { n: "01", t: "Compattezza", d: "Al tatto il tartufo deve risultare sodo e compatto, senza parti molli: il primo segno di freschezza e di una corretta conservazione." },
            { n: "02", t: "Profumo", d: "Intenso, pulito e tipico della specie: il profumo è l'indicatore più immediato della qualità di un tartufo fresco." },
            { n: "03", t: "Colore", d: "La gleba deve mostrare il colore caratteristico e venature definite: dal bianco nocciola dell'Alba al bruno intenso del nero pregiato." },
            { n: "04", t: "Grammatura", d: "Selezioniamo in base alle grammature più adatte ai molteplici utilizzi in linea con le esigenze dei nostri clienti. Dalle pezzature molto piccole (5g circa) a quelle grandi (oltre 500g al pz)." },
        ],
        note: "Ogni consegna avviene con furgoni a temperatura controllata, per mantenere intatte freschezza e caratteristiche fino alla tua cucina.",
        cta: "Richiedi disponibilità e listino",
        back: "Torna al sito",
    },
    en: {
        overline: "Truffles & Mushrooms",
        title: "The freshest truffle, selected for you",
        intro: "Every week we select the freshest truffle thanks to a network of around 50 collection points across Italy and abroad: we follow the seasonality of each territory to always guarantee the best product, from Alba white to prized and summer black.",
        criteriaTitle: "How we select the best fresh truffle",
        criteria: [
            { n: "01", t: "Firmness", d: "To the touch the truffle must be firm and compact, with no soft spots: the first sign of freshness and proper storage." },
            { n: "02", t: "Aroma", d: "Intense, clean and typical of the species: aroma is the most immediate indicator of a fresh truffle's quality." },
            { n: "03", t: "Colour", d: "The flesh must show its characteristic colour and defined veins: from the hazelnut white of Alba to the deep brown of prized black." },
            { n: "04", t: "Weight", d: "We select the weights best suited to every use, in line with our clients' needs. From very small pieces (around 5g) to large ones (over 500g each)." },
        ],
        note: "Every delivery is made with temperature-controlled vans, keeping freshness and characteristics intact until your kitchen.",
        cta: "Check availability & prices",
        back: "Back to site",
    },
    es: {
        overline: "Trufas y setas",
        title: "La trufa más fresca, seleccionada para ti",
        intro: "Cada semana seleccionamos la trufa más fresca gracias a una red de unos 50 puntos de recolección entre Italia y el extranjero: seguimos la temporada de cada territorio para garantizar siempre el mejor producto, de la blanca de Alba a la negra pregiada y de verano.",
        criteriaTitle: "Cómo seleccionamos la mejor trufa fresca",
        criteria: [
            { n: "01", t: "Compacidad", d: "Al tacto la trufa debe ser firme y compacta, sin partes blandas: la primera señal de frescura y de una conservación correcta." },
            { n: "02", t: "Perfume", d: "Intenso, limpio y típico de la especie: el perfume es el indicador más inmediato de la calidad de una trufa fresca." },
            { n: "03", t: "Color", d: "La gleba debe mostrar el color característico y vetas definidas: del blanco avellana de Alba al marrón intenso de la negra pregiada." },
            { n: "04", t: "Gramaje", d: "Seleccionamos según los gramajes más adecuados a los múltiples usos, en línea con las necesidades de nuestros clientes. Desde piezas muy pequeñas (unos 5g) hasta grandes (más de 500g por pieza)." },
        ],
        note: "Cada entrega se realiza con furgones de temperatura controlada, manteniendo intactas frescura y características hasta tu cocina.",
        cta: "Consultar disponibilidad y tarifa",
        back: "Volver al sitio",
    },
    ja: {
        overline: "トリュフ＆きのこ",
        title: "最高にフレッシュなアルバ産白トリュフを、あなたのために",
        intro: "毎週、イタリア国内外の約50か所の収集拠点ネットワークから最もフレッシュなトリュフを厳選しています。各地の旬を追いかけ、アルバ産白トリュフから上質な黒トリュフ、夏トリュフまで、常に最高の商品をお届けします。",
        criteriaTitle: "最高のフレッシュトリュフの選び方",
        criteria: [
            { n: "01", t: "コンパクトさ", d: "触れたときに硬く引き締まり、柔らかい部分がないこと：鮮度と適切な保存の最初の証です。" },
            { n: "02", t: "香り", d: "その種類特有の、強く澄んだ香りであること：香りはフレッシュトリュフの品質を示す最も直接的な指標です。" },
            { n: "03", t: "色", d: "果肉（グレーバ）が特有の色と明確な脈理を示すこと：アルバ産のヘーゼルナッツのような白から、上質な黒の深いブラウンまで。" },
            { n: "04", t: "グラム数", d: "お客様のニーズに沿った多様な用途に適したグラム数で選別します。約5gの小さなものから、1個500gを超える大きなものまで。" },
        ],
        note: "すべての配送は温度管理車両で行い、厨房に届くまで鮮度と特性をそのまま保持します。海外へは航空便も可能です。",
        cta: "入荷状況と価格表を請求",
        back: "サイトに戻る",
    },
};

export const TrufflePage = () => {
    const { lang, setLang } = useLanguage();
    const c = CONTENT[lang] || CONTENT.it;
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const goContact = () => {
        navigate("/");
        setTimeout(() => {
            const el = document.getElementById("contatti");
            if (!el) return;
            if (window.__lenis) window.__lenis.scrollTo(el, { offset: -64, duration: 1.4 });
            else el.scrollIntoView({ behavior: "smooth" });
        }, 600);
    };

    return (
        <div className="min-h-screen bg-[#1C2029] font-body text-white antialiased" data-testid="truffle-page">
            <header className="border-b border-white/10 bg-[#1C2029]/75 backdrop-blur-xl">
                <div className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-4 md:px-12 lg:px-24">
                    <img src="/assets/logo-white.png" alt="Midora Atelier Gastronomico" className="h-12 w-auto" />
                    <div className="flex items-center gap-5">
                        <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.2em]">
                            {["it", "en", "es", "ja"].map((l) => (
                                <button
                                    key={l}
                                    data-testid={`truffle-lang-${l}`}
                                    onClick={() => setLang(l)}
                                    className={`uppercase transition-colors duration-300 ${lang === l ? "text-midora-red" : "text-white/40 hover:text-white"}`}
                                >
                                    {l}
                                </button>
                            ))}
                        </div>
                        <Link
                            to="/"
                            data-testid="truffle-back-link"
                            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 transition-colors duration-300 hover:text-white"
                        >
                            <ArrowLeft className="h-4 w-4" /> {c.back}
                        </Link>
                    </div>
                </div>
            </header>

            <section className="relative flex min-h-[55vh] items-end overflow-hidden">
                <img src="/assets/hero-truffle.jpg" alt="Tartufo fresco" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: "center 40%" }} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C2029] via-[#1C2029]/55 to-[#1C2029]/25" />
                <div className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-16 pt-32 md:px-12 lg:px-24">
                    <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-white md:text-sm" data-testid="truffle-overline">
                        {c.overline}
                    </p>
                    <h1 className="max-w-3xl font-display text-4xl font-light leading-tight tracking-tighter sm:text-6xl" data-testid="truffle-title">
                        {c.title}
                    </h1>
                </div>
            </section>

            <main className="mx-auto max-w-[1600px] px-5 py-16 md:px-12 md:py-24 lg:px-24">
                <p className="max-w-3xl font-display text-2xl font-light italic leading-relaxed text-[#DBD1BD]/85 md:text-3xl" data-testid="truffle-intro">
                    {c.intro}
                </p>

                <h2 className="mt-20 font-display text-3xl font-light tracking-tighter sm:text-4xl" data-testid="truffle-criteria-title">
                    {c.criteriaTitle}
                </h2>

                <div className="mt-10 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
                    {c.criteria.map((item) => (
                        <div key={item.n} className="group bg-[#1C2029] p-8 transition-colors duration-500 hover:bg-[#232833]" data-testid={`truffle-criterion-${item.n}`}>
                            <span className="font-display text-5xl font-light leading-none text-midora-red/80">{item.n}</span>
                            <h3 className="mt-6 font-display text-2xl font-light italic tracking-tight">{item.t}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-[#DBD1BD]/60">{item.d}</p>
                            <span className="mt-6 block h-px w-0 bg-midora-red transition-all duration-700 group-hover:w-full" />
                        </div>
                    ))}
                </div>

                <div className="mt-14 border-l-2 border-midora-red pl-6">
                    <p className="max-w-2xl text-sm leading-relaxed text-[#DBD1BD]/70 md:text-base" data-testid="truffle-note">
                        {c.note}
                    </p>
                </div>

                <button
                    data-testid="truffle-cta"
                    onClick={goContact}
                    className="mt-12 inline-flex items-center gap-3 bg-midora-red px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#DBD1BD] hover:text-[#1C2029]"
                >
                    {c.cta} <ArrowRight className="h-4 w-4" />
                </button>
            </main>

            <footer className="border-t border-white/10 py-8">
                <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
                    © 2026 Midora Atelier Gastronomico
                </p>
            </footer>
        </div>
    );
};
